import React, { useEffect, useState, useCallback } from 'react';
import { trainingAPI } from '../../services/trainingAPI';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function EmployeeTraining({ employeeId }) {
  const [streams,       setStreams]       = useState([]);
  const [training,      setTraining]      = useState(null);   // current training record
  const [projects,      setProjects]      = useState([]);
  const [loading,       setLoading]       = useState(true);

  // assignment form
  const [showAssign,    setShowAssign]    = useState(false);
  const [assignForm,    setAssignForm]    = useState({ stream_id: '', start_date: '', status: 'active', supervisor_name: '', supervisor_email: '', notes: '' });
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignErr,     setAssignErr]     = useState('');

  // project form
  const [showProjForm,  setShowProjForm]  = useState(false);
  const [editProjId,    setEditProjId]    = useState(null);
  const [projForm,      setProjForm]      = useState({ title: '', description: '', github_url: '', summary: '', completed_date: '' });
  const [projLoading,   setProjLoading]   = useState(false);
  const [projErr,       setProjErr]       = useState('');

  // delete project
  const [deleteProjId,  setDeleteProjId]  = useState(null);
  const [deleteProjLoading, setDeleteProjLoading] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      trainingAPI.listStreams(),
      trainingAPI.getEmployee(employeeId),
    ]).then(([sRes, tRes]) => {
      setStreams(sRes.data.data.streams || []);
      setTraining(tRes.data.data.training || null);
      setProjects(tRes.data.data.projects || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => { loadData(); }, [loadData]);

  function openAssign() {
    setAssignForm({
      stream_id:        training?.stream_id  || (streams[0]?.id || ''),
      start_date:       training?.start_date || '',
      status:           training?.status     || 'active',
      supervisor_name:  training?.supervisor_name  || '',
      supervisor_email: training?.supervisor_email || '',
      notes:            training?.notes      || '',
    });
    setAssignErr('');
    setShowAssign(true);
  }

  async function handleAssign(e) {
    e.preventDefault();
    if (!assignForm.stream_id) { setAssignErr('Please select a stream.'); return; }
    if (!assignForm.start_date) { setAssignErr('Start date is required.'); return; }
    setAssignLoading(true); setAssignErr('');
    try {
      await trainingAPI.assign({ employee_id: Number(employeeId), stream_id: Number(assignForm.stream_id), ...assignForm });
      setShowAssign(false);
      loadData();
    } catch (ex) {
      setAssignErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setAssignLoading(false);
    }
  }

  function openAddProject() {
    setEditProjId(null);
    setProjForm({ title: '', description: '', github_url: '', summary: '', completed_date: '' });
    setProjErr('');
    setShowProjForm(true);
  }

  function openEditProject(p) {
    setEditProjId(p.id);
    setProjForm({
      title:          p.title || '',
      description:    p.description || '',
      github_url:     p.github_url || '',
      summary:        p.summary || '',
      completed_date: p.completed_date || '',
    });
    setProjErr('');
    setShowProjForm(true);
  }

  async function handleSaveProject(e) {
    e.preventDefault();
    if (!projForm.title.trim()) { setProjErr('Project title is required.'); return; }
    setProjLoading(true); setProjErr('');
    try {
      const payload = {
        employee_training_id: training.id,
        ...projForm,
      };
      if (editProjId) payload.id = editProjId;
      await trainingAPI.saveProject(payload);
      setShowProjForm(false);
      loadData();
    } catch (ex) {
      setProjErr(ex.response?.data?.message || 'Save failed.');
    } finally {
      setProjLoading(false);
    }
  }

  async function handleDeleteProject(id) {
    setDeleteProjLoading(true);
    try {
      await trainingAPI.deleteProject(id);
      setDeleteProjId(null);
      loadData();
    } catch (ex) {
      alert(ex.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleteProjLoading(false);
    }
  }

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Loading training details…</div>;

  return (
    <div className="space-y-5">

      {/* ── Stream Assignment ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800">Training Stream</h3>
            {training && (
              <p className="text-xs text-slate-500 mt-0.5">
                {training.stream_name} &nbsp;·&nbsp; Started {fmtDate(training.start_date)}
              </p>
            )}
          </div>
          <button onClick={openAssign}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
            {training ? 'Change Stream' : 'Assign Stream'}
          </button>
        </div>

        {!training && !showAssign && (
          <div className="px-5 py-8 text-center text-slate-400 text-sm">
            No training stream assigned yet.
          </div>
        )}

        {training && !showAssign && (
          <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Row label="Stream"      value={training.stream_name} />
            <Row label="Start Date"  value={fmtDate(training.start_date)} />
            <Row label="Status"      value={<StatusBadge v={training.status} />} />
            <Row label="Supervisor"  value={training.supervisor_name  || '—'} />
            <Row label="Sup. Email"  value={training.supervisor_email || '—'} />
            {training.notes && <div className="col-span-2 sm:col-span-3"><Row label="Notes" value={training.notes} /></div>}
          </div>
        )}

        {showAssign && (
          <form onSubmit={handleAssign} className="px-5 py-4 space-y-3 bg-slate-50/50 border-t border-slate-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Stream <span className="text-red-500">*</span></label>
                <select value={assignForm.stream_id} onChange={e => setAssignForm(p => ({ ...p, stream_id: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="">Select stream…</option>
                  {streams.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Start Date <span className="text-red-500">*</span></label>
                <input type="date" value={assignForm.start_date} onChange={e => setAssignForm(p => ({ ...p, start_date: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                <select value={assignForm.status} onChange={e => setAssignForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Supervisor Name</label>
                <input value={assignForm.supervisor_name} onChange={e => setAssignForm(p => ({ ...p, supervisor_name: e.target.value }))}
                  placeholder="Supervisor full name"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Supervisor Email</label>
                <input type="email" value={assignForm.supervisor_email} onChange={e => setAssignForm(p => ({ ...p, supervisor_email: e.target.value }))}
                  placeholder="supervisor@example.com"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <input value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Optional notes"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            {assignErr && <p className="text-red-600 text-xs">{assignErr}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowAssign(false)}
                className="flex-1 border border-slate-300 text-slate-600 py-2 rounded-lg text-sm hover:bg-white">Cancel</button>
              <button type="submit" disabled={assignLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 rounded-lg text-sm font-semibold">
                {assignLoading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Projects ── */}
      {training && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h3 className="font-semibold text-slate-800">Training Projects</h3>
              <p className="text-xs text-slate-500 mt-0.5">{projects.length} project{projects.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={openAddProject}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
              + Add Project
            </button>
          </div>

          {/* Project form */}
          {showProjForm && (
            <form onSubmit={handleSaveProject}
              className="border-b border-slate-100 px-5 py-4 space-y-3 bg-blue-50/30">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {editProjId ? `Edit Project` : `New Project ${projects.length + 1}`}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Title <span className="text-red-500">*</span></label>
                  <input value={projForm.title} onChange={e => setProjForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Project title"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                  <textarea value={projForm.description} onChange={e => setProjForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="What is this project about?"
                    rows={2}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">GitHub URL</label>
                  <input value={projForm.github_url} onChange={e => setProjForm(p => ({ ...p, github_url: e.target.value }))}
                    placeholder="https://github.com/..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Completed Date</label>
                  <input type="date" value={projForm.completed_date} onChange={e => setProjForm(p => ({ ...p, completed_date: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Summary</label>
                  <textarea value={projForm.summary} onChange={e => setProjForm(p => ({ ...p, summary: e.target.value }))}
                    placeholder="Key learnings, outcomes, notes…"
                    rows={3}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
              </div>
              {projErr && <p className="text-red-600 text-xs">{projErr}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowProjForm(false)}
                  className="flex-1 border border-slate-300 text-slate-600 py-2 rounded-lg text-sm hover:bg-white">Cancel</button>
                <button type="submit" disabled={projLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2 rounded-lg text-sm font-semibold">
                  {projLoading ? 'Saving…' : editProjId ? 'Update Project' : 'Add Project'}
                </button>
              </div>
            </form>
          )}

          {/* Project list */}
          {projects.length === 0 && !showProjForm ? (
            <div className="px-5 py-8 text-center text-slate-400 text-sm">No projects added yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {projects.map(p => (
                <div key={p.id} className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-700">{p.project_number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 text-sm">{p.title}</span>
                        {p.completed_date && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            Completed {fmtDate(p.completed_date)}
                          </span>
                        )}
                      </div>
                      {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                      {p.github_url && (
                        <a href={p.github_url} target="_blank" rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline mt-0.5 inline-block">
                          GitHub →
                        </a>
                      )}
                      {p.summary && (
                        <p className="text-xs text-slate-600 mt-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 italic">
                          {p.summary}
                        </p>
                      )}
                      <div className="flex gap-3 mt-1.5">
                        <button onClick={() => openEditProject(p)}
                          className="text-xs text-blue-600 hover:underline">Edit</button>
                        <button onClick={() => setDeleteProjId(p.id)}
                          className="text-xs text-red-500 hover:underline">Delete</button>
                      </div>
                    </div>
                  </div>

                  {deleteProjId === p.id && (
                    <div className="mt-2 ml-11 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs">
                      <span className="text-red-700 flex-1">Delete project {p.project_number}?</span>
                      <button onClick={() => setDeleteProjId(null)} disabled={deleteProjLoading}
                        className="border border-slate-300 text-slate-600 px-2 py-0.5 rounded hover:bg-white">Cancel</button>
                      <button onClick={() => handleDeleteProject(p.id)} disabled={deleteProjLoading}
                        className="bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700 font-semibold">
                        {deleteProjLoading ? '…' : 'Delete'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-slate-700">{value || '—'}</p>
    </div>
  );
}

function StatusBadge({ v }) {
  const cls = v === 'active' ? 'bg-green-100 text-green-700' :
              v === 'completed' ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${cls}`}>{v}</span>;
}
